import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { Icon } from '../components/Icon.tsx';
import {
  registerApi,
  type Business,
  type BusinessPatch,
  type FormalBusinessInput,
  type HomeBusinessInput,
  type PersonSummary,
} from '../features/register/api.ts';
import { CustomFields } from '../components/CustomFields.tsx';
import { type FormEntity } from '../features/forms/api.ts';
import { dv } from '../i18n/dv.ts';

interface AxiosLikeError { response?: { status?: number; data?: { error?: string } } }

function useHouse(houseId: string) {
  return useQuery({
    queryKey: ['house', houseId],
    queryFn: () => registerApi.house(houseId),
  });
}

const STATUS_PILL: Record<string, string> = {
  ACTIVE: 'pill-success',
  MOVED_OUT: 'pill-pending',
  DECEASED: 'pill-neutral',
};

function StatusPill({ status }: { status: PersonSummary['status'] }): JSX.Element {
  return (
    <span className={`pill shrink-0 ${STATUS_PILL[status] ?? 'pill-neutral'}`}>
      {dv.register.statusLabel[status]}
    </span>
  );
}

/** People section: list active persons (each links to their detail page) + quick add-by-name. */
function PeopleSection({ houseId, persons, ownerPersonId }: { houseId: string; persons: PersonSummary[]; ownerPersonId: string | null }): JSX.Element {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const add = useMutation({
    mutationFn: (fullName: string) => registerApi.addPerson(houseId, { fullName }),
    onSuccess: () => {
      // Just append to the list and stay on the house page; the user opens the
      // person from the list when they want to fill in the rest.
      setName('');
      setError(null);
      void qc.invalidateQueries({ queryKey: ['house', houseId] });
    },
    onError: () => setError(dv.common.error),
  });

  return (
    <section className="card-paper overflow-hidden">
      <header className="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
        <h2 className="font-serif text-lg font-bold leading-tight">{dv.register.persons}</h2>
        <span className="mono-meta text-muted-foreground">{persons.length}</span>
      </header>

      {persons.length === 0 ? (
        <p className="px-4 py-5 text-center text-sm text-muted-foreground">{dv.register.noPersons}</p>
      ) : (
        <ul className="divide-y divide-border">
          {persons.map((p) => (
            <li key={p.id}>
              <Link
                to="/people/$personId"
                params={{ personId: p.id }}
                className="flex items-center justify-between gap-3 p-3 transition-colors hover:bg-muted/50"
              >
                <div className="flex min-w-0 items-center gap-2">
                  <p className={`font-medium leading-snug truncate ${p.status === 'DECEASED' ? 'text-muted-foreground line-through' : ''}`}>
                    {p.fullName}
                  </p>
                  {p.id === ownerPersonId && (
                    <span className="pill shrink-0 before:hidden bg-[hsl(var(--secondary-container))] text-[hsl(var(--on-secondary-container))]">
                      <Icon name="workspace_premium" size={11} /> {dv.register.ownerBadge}
                    </span>
                  )}
                  <StatusPill status={p.status} />
                </div>
                <Icon name="chevron_left" size={16} className="shrink-0 text-muted-foreground" />
              </Link>
            </li>
          ))}
        </ul>
      )}

      <form
        className="border-t border-border p-3"
        onSubmit={(e) => {
          e.preventDefault();
          const trimmed = name.trim();
          if (!trimmed) return;
          setError(null);
          add.mutate(trimmed);
        }}
      >
        <div className="flex gap-2">
          <input
            className="input flex-1"
            placeholder={dv.register.addPersonName}
            value={name}
            onChange={(e) => setName(e.target.value)}
            aria-label={dv.register.addPersonName}
          />
          <button type="submit" disabled={add.isPending || !name.trim()} className="btn-primary shrink-0">
            <Icon name="add" size={15} /> {dv.register.addPerson}
          </button>
        </div>
        {error && <p className="mt-2 text-sm text-destructive" role="alert">{error}</p>}
      </form>
    </section>
  );
}

type BusinessKind = 'registered' | 'home';

interface BusinessConfig {
  heading: string;
  list: Business[];
  add: (body: { name: string; type?: string; ownerName?: string }) => Promise<Business>;
  close: (id: string) => Promise<Business>;
  withOwner: boolean;
  kind: BusinessKind;
}

interface BusinessFormState {
  name: string;
  type: string;
  ownerName: string;
}

/** Modal to edit a business (name / type / owner) plus its custom fields. */
function BusinessEditModal({
  houseId, businessId, kind, withOwner, onClose,
}: {
  houseId: string;
  businessId: string;
  kind: BusinessKind;
  withOwner: boolean;
  onClose: () => void;
}): JSX.Element {
  const qc = useQueryClient();
  const entity: FormEntity = kind === 'registered' ? 'REGISTERED_BUSINESS' : 'HOME_BUSINESS';

  const { data: business, isLoading } = useQuery({
    queryKey: ['business', kind, businessId],
    queryFn: () => (kind === 'registered'
      ? registerApi.business(businessId)
      : registerApi.homeBusiness(businessId)),
  });

  const [form, setForm] = useState<BusinessFormState | null>(null);
  const [customData, setCustomData] = useState<Record<string, unknown> | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Seed local state once the business loads.
  if (business && form === null) {
    setForm({ name: business.name, type: business.type ?? '', ownerName: business.ownerName ?? '' });
    setCustomData({ ...(business.customData ?? {}) });
  }

  const save = useMutation({
    mutationFn: () => {
      const patch: BusinessPatch = {
        name: (form?.name ?? '').trim(),
        type: (form?.type ?? '').trim() || null,
        customData: customData ?? {},
      };
      if (withOwner) patch.ownerName = (form?.ownerName ?? '').trim() || null;
      return kind === 'registered'
        ? registerApi.editBusiness(businessId, patch)
        : registerApi.editHomeBusiness(businessId, patch);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['house', houseId] });
      onClose();
    },
    onError: () => setError(dv.common.error),
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="card-paper w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-b-none sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
          <h2 className="font-serif text-lg font-bold leading-tight">{dv.register.editBusiness}</h2>
          <button onClick={onClose} className="btn-ghost px-2 py-1" aria-label={dv.common.close}>
            <Icon name="close" size={16} />
          </button>
        </header>

        {isLoading || !form ? (
          <p className="p-4 text-center text-sm text-muted-foreground" role="status">{dv.common.loading}</p>
        ) : (
          <form
            className="space-y-3 p-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (!form.name.trim()) return;
              setError(null);
              save.mutate();
            }}
          >
            <div className="space-y-1">
              <label className="field-label">{dv.register.businessName}</label>
              <input
                required
                className="input"
                value={form.name}
                onChange={(e) => setForm((f) => (f ? { ...f, name: e.target.value } : f))}
              />
            </div>
            <div className="space-y-1">
              <label className="field-label">{dv.register.businessType}</label>
              <input
                className="input"
                value={form.type}
                onChange={(e) => setForm((f) => (f ? { ...f, type: e.target.value } : f))}
              />
            </div>
            {withOwner && (
              <div className="space-y-1">
                <label className="field-label">{dv.register.businessOwner}</label>
                <input
                  className="input"
                  value={form.ownerName}
                  onChange={(e) => setForm((f) => (f ? { ...f, ownerName: e.target.value } : f))}
                />
              </div>
            )}

            <CustomFields
              entity={entity}
              value={customData ?? {}}
              onChange={setCustomData}
            />

            {error && <p className="text-sm text-destructive" role="alert">{error}</p>}

            <div className="flex gap-2">
              <button type="submit" disabled={save.isPending || !form.name.trim()} className="btn-primary">
                {save.isPending ? dv.common.saving : dv.common.save}
              </button>
              <button type="button" onClick={onClose} className="btn-ghost">{dv.common.cancel}</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function BusinessGroup({ houseId, config }: { houseId: string; config: BusinessConfig }): JSX.Element {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [type, setType] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const invalidate = () => qc.invalidateQueries({ queryKey: ['house', houseId] });

  const add = useMutation({
    mutationFn: () => config.add({
      name: name.trim(),
      type: type.trim() || undefined,
      ownerName: config.withOwner ? (ownerName.trim() || undefined) : undefined,
    }),
    onSuccess: () => {
      setAdding(false); setName(''); setType(''); setOwnerName(''); setError(null);
      void invalidate();
    },
    onError: () => setError(dv.common.error),
  });
  const close = useMutation({
    mutationFn: (id: string) => config.close(id),
    onSuccess: () => { setError(null); void invalidate(); },
    onError: () => setError(dv.common.error),
  });

  const active = config.list.filter((b) => b.active);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="status-label text-muted-foreground">{config.heading}</h3>
        {!adding && (
          <button onClick={() => { setAdding(true); setError(null); }} className="btn-ghost px-2 py-1 text-xs">
            <Icon name="add" size={14} /> {dv.register.addBusiness}
          </button>
        )}
      </div>

      {active.length === 0 && !adding ? (
        <p className="text-sm text-muted-foreground">{dv.register.noBusinesses}</p>
      ) : (
        <ul className="divide-y divide-border rounded-lg border border-border">
          {active.map((b) => (
            <li key={b.id} className="flex items-center justify-between gap-2 px-3 py-2">
              <button
                type="button"
                onClick={() => { setError(null); setEditingId(b.id); }}
                className="min-w-0 flex-1 text-start"
                aria-label={dv.register.editBusiness}
              >
                <p className="font-medium leading-snug truncate">{b.name}</p>
                {(b.type || b.ownerName) && (
                  <p className="mono-meta text-muted-foreground truncate" dir="auto">
                    {[b.type, b.ownerName].filter(Boolean).join(' · ')}
                  </p>
                )}
              </button>
              <button
                onClick={() => { setError(null); close.mutate(b.id); }}
                disabled={close.isPending}
                className="btn-ghost px-2 py-1 text-xs shrink-0"
                aria-label={dv.register.closeBusiness}
              >
                <Icon name="close" size={14} /> {dv.register.closeBusiness}
              </button>
            </li>
          ))}
        </ul>
      )}

      {adding && (
        <form
          className="space-y-2 rounded-lg border border-border bg-muted/20 p-3"
          onSubmit={(e) => { e.preventDefault(); if (!name.trim()) return; setError(null); add.mutate(); }}
        >
          <div className="space-y-1">
            <label className="field-label">{dv.register.businessName}</label>
            <input required className="input" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            <div className="space-y-1">
              <label className="field-label">{dv.register.businessType}</label>
              <input className="input" value={type} onChange={(e) => setType(e.target.value)} />
            </div>
            {config.withOwner && (
              <div className="space-y-1">
                <label className="field-label">{dv.register.businessOwner}</label>
                <input className="input" value={ownerName} onChange={(e) => setOwnerName(e.target.value)} />
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <button type="submit" disabled={add.isPending || !name.trim()} className="btn-primary">
              {add.isPending ? dv.common.saving : dv.common.save}
            </button>
            <button type="button" onClick={() => { setAdding(false); setError(null); }} className="btn-ghost">
              {dv.common.cancel}
            </button>
          </div>
        </form>
      )}

      {error && <p className="text-sm text-destructive" role="alert">{error}</p>}

      {editingId && (
        <BusinessEditModal
          houseId={houseId}
          businessId={editingId}
          kind={config.kind}
          withOwner={config.withOwner}
          onClose={() => setEditingId(null)}
        />
      )}
    </div>
  );
}

export function HouseRegister(): JSX.Element {
  const { houseId } = useParams({ from: '/houses/$houseId' });
  const { data: house, isLoading, isError, error: queryError } = useHouse(houseId);

  if (isLoading) {
    return <div className="page"><p className="status-label text-muted-foreground" role="status">{dv.common.loading}</p></div>;
  }
  if (isError || !house) {
    const notFound = (queryError as AxiosLikeError)?.response?.status === 404;
    return (
      <div className="page space-y-4">
        <Link to="/houses" className="btn-ghost"><Icon name="arrow_forward" size={15} /> {dv.houses.title}</Link>
        <p className="card-paper p-6 text-center text-sm text-muted-foreground">
          {notFound ? dv.register.notFound : dv.common.error}
        </p>
      </div>
    );
  }

  return (
    <div className="page space-y-6">
      <Link to="/houses" className="btn-ghost"><Icon name="arrow_forward" size={15} /> {dv.houses.title}</Link>

      <header>
        <h1 className="font-serif text-3xl tracking-tight">{house.name}</h1>
        <p className="mono-meta text-muted-foreground mt-1">
          {[house.number != null ? `${dv.houses.numberShort} ${house.number}` : null, house.ward, house.address].filter(Boolean).join(' · ') || dv.common.none}
        </p>
      </header>

      <PeopleSection houseId={houseId} persons={house.persons} ownerPersonId={house.ownerPersonId} />

      <section className="card-paper space-y-5 p-4">
        <h2 className="font-serif text-lg font-bold leading-tight">{dv.register.businesses}</h2>
        <BusinessGroup
          houseId={houseId}
          config={{
            heading: dv.register.houseBusinesses,
            list: house.houseBusinesses,
            add: (body: FormalBusinessInput) => registerApi.addBusiness(houseId, body),
            close: (id: string) => registerApi.closeBusiness(id),
            withOwner: true,
            kind: 'registered',
          }}
        />
        <BusinessGroup
          houseId={houseId}
          config={{
            heading: dv.register.homeBusinesses,
            list: house.businesses,
            add: (body: HomeBusinessInput) => registerApi.addHomeBusiness(houseId, body),
            close: (id: string) => registerApi.closeHomeBusiness(id),
            withOwner: false,
            kind: 'home',
          }}
        />
      </section>
    </div>
  );
}
